import { Dropdown, IconButton, Icons, Tooltip } from '@commenda/kakashi';
import { useReactFlow } from '@xyflow/react';
import { forwardRef, type CSSProperties } from 'react';

interface DiagramToolbarProps {
  onExportPng?: () => void;
  onExportPdf?: () => void;
  onCopyPng?: () => void;
  onFitView: () => void;
}

// Inline hex so values survive consuming app's Tailwind content-scanner.
const TOOLBAR_BORDER = '#DEDBD4';
const TOOLBAR_BG = '#FFFFFF';
const ICON_HOVER_BG = '#F8F6F1';
const ICON_COLOR = '#57524D';

const containerStyle: CSSProperties = {
  borderColor: TOOLBAR_BORDER,
  borderWidth: 0.5,
  borderStyle: 'solid',
  backgroundColor: TOOLBAR_BG,
  borderRadius: 8,
  padding: 4,
  boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
};

// kakashi IconButton's `default` variant flips icon color to brand on hover;
// override so icon stays text-secondary and bg = cream throughout.
const buttonStyle: CSSProperties = {
  borderRadius: 4,
  color: ICON_COLOR,
};

const rightGroupStyle: CSSProperties = {
  ...containerStyle,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

// Must render inside <ReactFlowProvider> — reads viewport API via useReactFlow.
const DiagramToolbar = ({
  onExportPng,
  onExportPdf,
  onCopyPng,
  onFitView,
}: DiagramToolbarProps) => {
  const { zoomIn, zoomOut } = useReactFlow();

  return (
    <div
      className="absolute z-10"
      style={{
        bottom: 12,
        right: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      {(onExportPng || onExportPdf || onCopyPng) && (
        <div style={containerStyle}>
          <Dropdown
            align="end"
            sideOffset={8}
            triggerTooltip="Export"
            menu={{
              groups: [
                {
                  items: [
                    ...(onExportPng
                      ? [{
                          label: 'Export as PNG',
                          icon: Icons.Image01,
                          onClick: onExportPng,
                        }]
                      : []),
                    ...(onExportPdf
                      ? [{
                          label: 'Export as PDF',
                          icon: Icons.File05,
                          onClick: onExportPdf,
                        }]
                      : []),
                    ...(onCopyPng
                      ? [{
                          label: 'Copy as PNG',
                          icon: Icons.Copy01,
                          onClick: onCopyPng,
                        }]
                      : []),
                  ],
                },
              ],
            }}
          >
            <ToolbarIconButton
              aria-label="Export"
              icon={<Icons.Download01 width={16} height={16} />}
            />
          </Dropdown>
        </div>
      )}
      <div style={rightGroupStyle}>
        <Tooltip content="Zoom out" asChild>
          <ToolbarIconButton
            aria-label="Zoom out"
            onClick={() => zoomOut({ duration: 200 })}
            icon={<Icons.ZoomOut width={16} height={16} />}
          />
        </Tooltip>
        <Tooltip content="Zoom in" asChild>
          <ToolbarIconButton
            aria-label="Zoom in"
            onClick={() => zoomIn({ duration: 200 })}
            icon={<Icons.ZoomIn width={16} height={16} />}
          />
        </Tooltip>
        <Tooltip content="Fit to view" asChild>
          <ToolbarIconButton
            aria-label="Fit to view"
            onClick={onFitView}
            icon={<Icons.Grid03 width={16} height={16} />}
          />
        </Tooltip>
      </div>
    </div>
  );
};

type ToolbarIconButtonProps = {
  icon: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  'aria-label': string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const ToolbarIconButton = forwardRef<HTMLButtonElement, ToolbarIconButtonProps>(
  function ToolbarIconButton({ icon, onClick, ...rest }, ref) {
  return (
    <IconButton
      ref={ref}
      size="lg"
      icon={icon}
      style={buttonStyle}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = ICON_HOVER_BG;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '';
      }}
      {...rest}
    />
  );
  },
);

export { DiagramToolbar };
